import { ViewportScroller, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, OnDestroy, SimpleChanges, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { auditTime, Subject, takeUntil } from 'rxjs';
import { FormKeys, FormName } from '@nx-customer-apps/shared/enums';
import { FormCacheConfig, FormFieldHighlights, RemovalTarget, SameFormValue } from '@nx-customer-apps/shared/interfaces';
import { FormStateService, QuestionControlService } from '@nx-customer-apps/shared/services';
import { Question, QuestionBase, QuestionGroup, RadioQuestion, SingleQuestion } from '@nx-customer-apps/shared/utils';
import { ComponentsModule, ConfirmConfig, LodashService } from '@nx-customer-apps/components';
import { QuestionComponent } from '../question/question.component';
import { MatButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'vp-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    imports: [ReactiveFormsModule, QuestionComponent, MatButton, ComponentsModule, NgTemplateOutlet, TranslatePipe]
})
export class FormComponent implements OnChanges, OnDestroy {
    private questionControl = inject(QuestionControlService);
    private viewportScroller = inject(ViewportScroller);
    formStateService = inject(FormStateService);

    /**
     * Helps to indentify the form whithin the other forms.
     * For ex. To inform parent component about edition state @see FormStateService
     */
    @Input() name: FormName;
    @Input('cacheConfig') onSubmitCacheConfig: FormCacheConfig | undefined;
    @Input() questions: Question[];
    @Input() customConfirmEnabled: boolean;
    @Input() customSubmitEnabled: boolean;
    @Input() highlights: FormFieldHighlights = {};

    private _customConfirm: ConfirmConfig;
    @Input() set customConfirm(value: ConfirmConfig) {
        this._customConfirm = value;
    }
    get customConfirm(): ConfirmConfig {
        return { ...this._customConfirm, disabled: this._customConfirm.skipDisabled ? false : !this.form.valid };
    }

    @Output() value: EventEmitter<any> = new EventEmitter();
    @Output() sameValue: EventEmitter<SameFormValue> = new EventEmitter();
    @Output() valueChange: EventEmitter<any> = new EventEmitter();
    @Output() afterQuestionsPatch: EventEmitter<{ [key: string]: FormControl }> = new EventEmitter();
    @Output() afterQuestionsAdd: EventEmitter<{ [key: string]: FormControl }> = new EventEmitter();
    @Output() afterQuestionsRemove: EventEmitter<FormKeys[]> = new EventEmitter();
    @Output() afterRetriveCache: EventEmitter<any> = new EventEmitter();
    @Output() formInitialized: EventEmitter<any> = new EventEmitter();
    @Output() questionTrigger: EventEmitter<{ key: FormKeys; value: any }> = new EventEmitter();
    @Output() onCustomConfirm: EventEmitter<void> = new EventEmitter();
    public form: FormGroup;
    public isSubmited: boolean = false;
    private destroy$: Subject<void> = new Subject();
    /**
     * Caches the last submited form value and questions configuration.
     * It helps to bring back the last submited form value and state when user provides changes to the form and then decides to withdraw the changes.
     */
    private onSubmitCache: { formValue?: any; questions?: Question[] } | null;
    /**
     * Caches the last provided form value.
     * It helps to bring back the last user's provided value in the questions which were removed and then added back to the form.
     */
    private onChangeCache: { formValue?: any } = { formValue: {} };

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['questions']) {
            this.questions = this.questionControl.addFormInternalAttributes(this.questions);
            this.form = this.questionControl.toFormGroup(this.questions);
            this.onValueChange();
            this.formInitialized.emit(this.form);
            this.initCache();
        }
        if (changes['cacheConfig']) {
            this.initCache();
        }
    }

    public addQuestions(questions: Question[]): void {
        const questionsToAdd = this.getQuestionsToAdd(questions);

        if (!questionsToAdd.length) {
            return;
        }

        const questionsToAddUpdatedWithCachedValue = this.getQuestionsUpdatedWithCacheValue(questionsToAdd);
        const questionsToAddWithAttributes = this.questionControl.addFormInternalAttributes(questionsToAddUpdatedWithCachedValue);
        const newForm = this.questionControl.toFormGroup(questionsToAddWithAttributes);

        this.afterQuestionsAdd.emit(newForm.controls as { [key: string]: FormControl });
        this.addControlsToForm(newForm, questionsToAddWithAttributes);
        this.addQuestionsToCorrespondingGroups(questionsToAddWithAttributes);
    }

    private getQuestionsUpdatedWithCacheValue(allQuestions: Question[]): Question[] {
        return allQuestions.map(question => {
            const singleQuestion = question as SingleQuestion;
            const cacheFallbackValue = singleQuestion.parentGroup
                ? this.onChangeCache.formValue[singleQuestion.parentGroup]?.[singleQuestion.key]
                : this.onChangeCache.formValue[singleQuestion.key];
            const value = singleQuestion.value || cacheFallbackValue;

            // If a question is a type of RadioQuestion we need to make sure it has to have the cached value within its options.
            const radioQuestion = question as RadioQuestion;
            const hasOptionOfCachedValue = radioQuestion.options?.find((option: { value: any }) => option.value === value);

            if (!radioQuestion.options || hasOptionOfCachedValue) {
                return { ...question, value };
            }

            return question;
        });
    }

    public removeQuestions(removalTargets: (FormKeys | RemovalTarget)[]): void {
        removalTargets.forEach(target => {
            const targetWithParent = target as RemovalTarget;
            if (targetWithParent.parentKey) {
                this.removeSubQuestion(removalTargets as RemovalTarget[], targetWithParent);
            } else {
                this.removeQuestionOrQuestionGroup(removalTargets as FormKeys[], target as FormKeys);
            }
        });

        const controlNames = removalTargets.map(targetItem => (targetItem as RemovalTarget).key || targetItem);
        this.afterQuestionsRemove.emit(controlNames);
    }

    public patchQuestions(questions: Question[]): void {
        const questionsToPatch = new Map();
        const questionsWithAttributes = this.questionControl.addFormInternalAttributes(questions);
        questionsWithAttributes.forEach(question => {
            const formKey = this.questionControl.getQuestionFormKey(question);
            questionsToPatch.set(formKey, question);
        });

        this.questions = this.updateControlsState(this.questions, questionsToPatch);

        const formKeys = Array.from(questionsToPatch.keys());
        const patchedControls = formKeys.reduce((acc, curr) => {
            const currentQuestion = questionsToPatch.get(curr) as SingleQuestion;
            if (currentQuestion.parentGroup) {
                acc[curr] = (this.form.get(currentQuestion.parentGroup) as FormGroup)?.controls[curr];
            } else {
                acc[curr] = this.form.controls[curr];
            }
            return acc;
        }, {});
        this.afterQuestionsPatch.emit(patchedControls as { [key: string]: FormControl });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onSubmit(): void {
        this.validate(this.form);
        if (!this.form.valid || (this.isValueDifferentThanCache && this.onSubmitCacheConfig?.updateOnConfim)) {
            return;
        }
        const value = this.form.getRawValue();
        if (this.isValueSameAsCache) {
            this.sameValue.next({ formName: this.name, value });
            return;
        }
        this.setOnSubmitCache();
        this.formStateService.setEditedForm(null);
        this.value.next(value);
        this.isSubmited = true;
    }

    public updateCacheAndSubmit(): void {
        this.setOnSubmitCache();
        this.formStateService.setEditedForm(null);
        this.validate(this.form);
        if (!this.form.valid || this.isValueDifferentThanCache) {
            return;
        }
        const value = this.form.getRawValue();
        this.value.next(value);
        this.isSubmited = true;
    }

    public restoreValueFromCache() {
        if (!this.onSubmitCache) {
            return;
        }
        this.questions = this.onSubmitCache.questions!;
        this.form = this.questionControl.toFormGroup(this.questions);
        this.onValueChange();
        this.form.patchValue(this.onSubmitCache.formValue);
        this.afterRetriveCache.emit(this.form);
        this.emitFormNameOnEdition();
    }

    public get isValueSameAsCache() {
        return this.onSubmitCache && LodashService.isEqual(this.onSubmitCache.formValue, this.form.getRawValue());
    }

    public get isValueDifferentThanCache() {
        return this.onSubmitCache && !LodashService.isEqual(this.onSubmitCache.formValue, this.form.getRawValue());
    }

    private updateQuestionValue(question: QuestionBase<any>): void {
        if (LodashService.isNil(question.value)) {
            return;
        }

        const formGroup = (question as SingleQuestion).parentGroup ? this.form.get((question as SingleQuestion).parentGroup!) : this.form;
        const control = formGroup!.get(question.key);
        control?.reset(question.value);
    }

    private updateQuestionValidators(question: QuestionBase<any>): void {
        if (LodashService.isNil(question.validators)) {
            return;
        }
        const formGroup = (question as SingleQuestion).parentGroup ? this.form.get((question as SingleQuestion).parentGroup!) : this.form;
        const control = formGroup!.get(question.key);
        control?.setValidators(question.validators);
    }

    private onValueChange(): void {
        this.form.valueChanges.pipe(auditTime(10), takeUntil(this.destroy$)).subscribe(value => {
            this.valueChange.emit(value);
            this.setOnChangeCache(value);
            this.emitFormNameOnEdition();
        });
    }

    private setOnChangeCache(formValue: { [key: string]: any }) {
        if (!formValue) {
            return;
        }

        Object.keys(formValue).forEach(key => {
            if (typeof formValue[key] === 'object') {
                this.setOnChangeCache(formValue[key]);
                return;
            }

            this.onChangeCache.formValue[key] = formValue[key] || this.onChangeCache.formValue[key];
        });
    }

    private setOnSubmitCache() {
        if (!this.onSubmitCacheConfig?.enabled) {
            return;
        }
        this.onSubmitCache = this.onSubmitCache || {};
        const formValue = this.form.getRawValue();
        this.onSubmitCache.formValue = formValue;
        this.onSubmitCache.questions = LodashService.cloneDeep(this.questions);
    }

    private validate(group: FormGroup): void {
        const controlNames: string[] = Object.keys(group.controls);
        let firstInvalidQuestionFormKey: string | undefined;
        controlNames.forEach(name => {
            const control = group.controls[name];
            if (control instanceof FormGroup) {
                this.validate(control as FormGroup);
                return;
            }

            control.updateValueAndValidity({ emitEvent: false });
            control.markAsTouched();
            if (!firstInvalidQuestionFormKey && control.errors) {
                firstInvalidQuestionFormKey = name;
            }
        });
        if (firstInvalidQuestionFormKey) {
            this.viewportScroller.scrollToAnchor(firstInvalidQuestionFormKey);
        }
    }

    /**
     * Emits form name when it's beeing edited and resets the value when the form restores its cached value @see FormStateService
     */
    private emitFormNameOnEdition() {
        if (this.isValueDifferentThanCache) {
            this.formStateService.setEditedForm(this.name);
        } else {
            this.formStateService.setEditedForm(null);
        }
    }

    private initCache() {
        if (this.onSubmitCacheConfig?.cacheInitialValue && this.form.valid) {
            this.setOnSubmitCache();
            return;
        }
        this.onSubmitCache = null;
    }

    /**
     * Filters out questions which are already in the form. Returns questions which are new to the form.
     */
    private getQuestionsToAdd(questions: Question[]) {
        return questions.filter(question => {
            const newQuestionFormKey = this.questionControl.getQuestionFormKey(question);
            const hasQuestionsToAdd = (questionFormKeysSource: Question[]) => {
                const questionFormKeys = questionFormKeysSource.map(oldQuestion => this.questionControl.getQuestionFormKey(oldQuestion));
                return !questionFormKeys.includes(newQuestionFormKey);
            };

            if (!(question as SingleQuestion).parentGroup) {
                return hasQuestionsToAdd(this.questions);
            }
            const parentQuestion: QuestionGroup = this.questions.find(
                currentQuestion => (currentQuestion as QuestionGroup).groupName == (question as SingleQuestion).parentGroup
            ) as QuestionGroup;

            if (!parentQuestion) {
                return;
            }

            return hasQuestionsToAdd(parentQuestion.questions);
        });
    }

    /**
     * New form controls from temporary newForm object are added to the real form (this.form).
     * @param newForm A temporary form structure which holds references for new controls.
     * @param questionsToAddWithAttributes Question configuration objects extended by form internal attributes.
     */
    private addControlsToForm(newForm: FormGroup, questionsToAddWithAttributes: Question[]): void {
        Object.keys(newForm.controls).forEach(controlKey => {
            const control = newForm.controls[controlKey];
            const correspondingQuestion: SingleQuestion = questionsToAddWithAttributes.find(
                questionItem => (questionItem as SingleQuestion).key === controlKey
            ) as SingleQuestion;

            const correspondingFormGroup: FormGroup = correspondingQuestion?.parentGroup
                ? (this.form.get(correspondingQuestion.parentGroup) as FormGroup)
                : this.form;

            correspondingFormGroup?.addControl(controlKey, control, { emitEvent: false });
            if (control.value) {
                control.updateValueAndValidity();
            }
        });
    }

    /**
     * New questions are added to the "main question" config (this.questions) and are sorted to the correct form groups.
     * @param questionsToAddWithAttributes Question configuration objects extended by form internal attributes.
     */
    private addQuestionsToCorrespondingGroups(questionsToAddWithAttributes: Question[]) {
        this.questions = questionsToAddWithAttributes
            .reduce((acc, curr) => {
                if ((curr as SingleQuestion).parentGroup) {
                    const targetQuestionGroup: QuestionGroup = acc.find(
                        questionItem => (questionItem as QuestionGroup).groupName === (curr as SingleQuestion).parentGroup
                    ) as QuestionGroup;
                    const subquestions = [...targetQuestionGroup!.questions, curr] as SingleQuestion[];
                    targetQuestionGroup!.questions = subquestions.sort((a, b) => a.order - b.order);
                    return acc;
                }
                acc.push(curr);
                return acc;
            }, this.questions)
            .sort((a, b) => a.order - b.order);
    }

    /**
     * Updates the state of questions to patch (a value, and their pristine/touched state).
     */
    private updateControlsState(questions: Question[], questionsToPatch: Map<string, QuestionBase<any>>): Question[] {
        return questions
            .map(question => {
                const questionsGroup = question as QuestionGroup;
                if (questionsGroup.questions) {
                    questionsGroup.questions = this.updateControlsState(questionsGroup.questions, questionsToPatch) as SingleQuestion[];
                }

                const formKey = this.questionControl.getQuestionFormKey(question);
                if (!questionsToPatch.has(formKey)) {
                    return question;
                }
                const patch = questionsToPatch.get(formKey)!;
                this.updateQuestionValue(patch);
                this.updateQuestionValidators(patch);
                return { ...question, ...patch };
            })
            .sort((a, b) => a.order - b.order);
    }

    private removeQuestionOrQuestionGroup(removalTargets: FormKeys[], target: FormKeys) {
        this.questions = this.questions.filter(question => {
            const formKey = this.questionControl.getQuestionFormKey(question);
            return !removalTargets.includes(formKey as FormKeys);
        });
        this.form.removeControl(target as FormKeys, { emitEvent: false });
    }

    private removeSubQuestion(removalTargets: RemovalTarget[], target: RemovalTarget) {
        const correspondingQuestionGroup = this.questions.find(
            questionItem => (questionItem as QuestionGroup).groupName === target.parentKey
        ) as QuestionGroup;

        correspondingQuestionGroup.questions = correspondingQuestionGroup.questions.filter(question => {
            const formKey = this.questionControl.getQuestionFormKey(question);
            return !removalTargets.find(target => {
                const targetItemWithParent = target as RemovalTarget;
                return targetItemWithParent.key === formKey;
            });
        }) as any;

        (this.form.get(target.parentKey) as FormGroup)?.removeControl(target.key, { emitEvent: false });
    }
}
